import { initContract } from '@ts-rest/core';
import { z } from 'zod';

// Chapter 2 References (TAN-4957). Reference links are deterministic,
// block-anchored pointers from threads to external canonical identities
// (skills, plan artifacts, files, PRs). See
// docs/plans/2026-07-09-tan-4957-references-plan.md.

const c = initContract();

const errorSchema = z.object({ message: z.string() });

export const referenceTypeSchema = z.enum([
  'skill',
  'slash_command',
  'plan_artifact',
  'file',
  'pr',
]);
export type ReferenceType = z.infer<typeof referenceTypeSchema>;

export const referenceDetectionChannelSchema = z.enum([
  'tool_use',
  'command_tag',
  'text_citation',
  'pr_link_block',
]);

export const referenceLinkDetailSchema = z
  .object({
    repository: z.string().optional(),
    number: z.number().optional(),
    repo: z.string().optional(),
  })
  .nullable();

// One deterministic reference witnessed by a specific thread block.
export const referenceLinkResourceSchema = z.object({
  type: z.literal('reference_link'),
  id: z.string().min(1),
  thread_id: z.string().min(1),
  block_id: z.string().min(1),
  reference_type: referenceTypeSchema,
  reference_key: z.string().min(1),
  detected_via: referenceDetectionChannelSchema,
  detail: referenceLinkDetailSchema,
  detected_at: z.string(),
});
export type ReferenceLinkResource = z.infer<typeof referenceLinkResourceSchema>;

// Per-thread references are an unpaginated child list (like
// `/threads/:id/decisions`): a thread's blocks are bounded, and the panel
// renders them all in transcript order (block index ascending).
export const referenceLinkListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('reference_link'),
  objects: z.array(referenceLinkResourceSchema),
});
export type ReferenceLinkListResponse = z.infer<
  typeof referenceLinkListResponseSchema
>;

export const listThreadReferencesQuerySchema = z.object({
  reference_type: referenceTypeSchema.optional(),
});
export type ListThreadReferencesQuery = z.infer<
  typeof listThreadReferencesQuerySchema
>;

export const listReferenceThreadsQuerySchema = z.object({
  reference_type: referenceTypeSchema,
  reference_key: z.string().min(1).max(1024),
  /** Cursor: a thread id; the response continues from the next-older thread. */
  before: z.string().min(1).max(64).optional(),
});
export type ListReferenceThreadsQuery = z.infer<
  typeof listReferenceThreadsQuerySchema
>;

// The reverse-lookup endpoint returns the canonical thread-list envelope.
// `index.ts` imports `referencesContract` from this file, so we can't pull
// `threadListResponseSchema` back in without a module-eval cycle (same
// constraint favorites.ts documents). We declare the envelope here and
// treat each row as `z.unknown()`; the API handler builds a fully-typed
// `ThreadListResponse` via `toThreadListObject`.
const referenceThreadListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('thread'),
  has_more: z.boolean(),
  objects: z.array(z.unknown()),
});

export const referencesContract = c.router({
  listThreadReferences: {
    method: 'GET',
    path: '/threads/:id/references',
    pathParams: z.object({ id: z.string().min(1) }),
    headers: z.object({ authorization: z.string().min(1).optional() }),
    query: listThreadReferencesQuerySchema,
    responses: {
      200: referenceLinkListResponseSchema,
      401: errorSchema,
      404: errorSchema,
    },
    summary:
      'List a thread\'s deterministic reference links (skills, plan artifacts, files, PRs), block-anchored, in transcript order.',
  },
  listReferenceThreads: {
    method: 'GET',
    path: '/references/threads',
    headers: z.object({ authorization: z.string().min(1).optional() }),
    query: listReferenceThreadsQuerySchema,
    responses: {
      200: referenceThreadListResponseSchema,
      400: errorSchema,
      401: errorSchema,
    },
    summary:
      'Reverse lookup: threads that witness a given reference (type + key), newest activity first, filtered to the viewer\'s visible threads.',
  },
});
